import { groupBy, uniqBy } from 'lodash';
import { AutocompletePredictionPredictionCategory } from 'utils/google-maps/types';
import { createSearchPredictionModel } from 'data/search-predictions';
import { isProvinceOrStateIncorrectlyMarkedAsCity } from 'hooks/use-location-search';
import type SearchPrediction from 'data/search-predictions';
import { ThemeNames } from 'types/themes';
import { searchProvider } from 'hooks/use-location-search';

/**
 * Deduplicates predictions by description and preserves location IDs from Google
 */
export const uniqByDescriptionAndAddLocationId = (predictions: SearchPrediction[]): SearchPrediction[] => {
  const resultMap: { [description: string]: SearchPrediction } = {};
  for (const prediction of predictions) {
    const descriptionKey = prediction.description.toLowerCase();
    if (descriptionKey in resultMap) {
      const existingLocation = resultMap[descriptionKey];
      if (prediction.source === searchProvider.GOOGLE) {
        existingLocation.locationId = prediction.id;
        existingLocation.source = prediction.source;
      }
    } else {
      resultMap[descriptionKey] = { ...prediction } as SearchPrediction;
    }
  }
  return Object.values(resultMap);
};

/**
 * Models city predictions consistently
 */
export const modelingCities = (cities: SearchPrediction[]): SearchPrediction[] => 
  cities.map(createSearchPredictionModel);

/**
 * Processes and combines predictions from go-search and Google
 */
export const processPredictions = (
  goSearchPredictions: SearchPrediction[],
  googleSearchPredictions: SearchPrediction[],
  isHomeAppraisalSearch: boolean = false
): SearchPrediction[] => {
  const predictionsGroupedByGroup = groupBy([...goSearchPredictions, ...googleSearchPredictions], 'group');
  const { listings = [], schools = [], buildings = [], locations = [], militaryBases = []} = predictionsGroupedByGroup;

  const locationsGroupedByLabel = groupBy(locations, 'label');
  const { city = [], neighbourhood = [], street = []} = locationsGroupedByLabel;
  
  // Remove duplicate cities that could come from the go-search and preserve locationId from Google
  const citiesWithLocationIds = uniqByDescriptionAndAddLocationId(city);
  const uniqCities = modelingCities(citiesWithLocationIds);
  
  // Filter out provinces/states incorrectly marked as cities
  const filteredCities = uniqCities.filter(city => !isProvinceOrStateIncorrectlyMarkedAsCity(city));
  
  // Remove duplicates that could come from the SearchPredictionStore
  const UNIQUENESS_ATTRIBUTE = 'description';
  const uniqNeighborhoods = uniqBy(neighbourhood, UNIQUENESS_ATTRIBUTE);
  const uniqStreets = uniqBy(street, UNIQUENESS_ATTRIBUTE);

  let finalPredictions: SearchPrediction[] = [];

  if (isHomeAppraisalSearch) {
    finalPredictions = [
      ...listings,
      ...filteredCities,
      ...uniqStreets,
    ];
  } else {
    finalPredictions = [
      ...militaryBases,
      ...listings,
      ...schools,
      ...buildings,
      ...filteredCities,
      ...uniqNeighborhoods,
      ...uniqStreets,
    ];
  }

  return finalPredictions;
}; 